import Box from '@mui/material/Box';
import { type SxProps, type Theme } from '@mui/material/styles';

import {
  CRISTA_LOADER_BLINK_MS,
  CRISTA_LOADER_CYCLE_MS,
  CRISTA_LOADER_DRAWS,
  cristaLoaderBlink,
  getCristaLoaderInk,
  getCristaLoaderRingCount,
  getCristaLoaderRings,
  getCristaLoaderStroke,
  type CristaSpinnerVariant,
} from '@/components/Loaders/utils';
import { controlTokens } from '@/theme/themePrimitives';

interface CristaSpinnerProps {
  /** 16 field · 20 button · 24 list · 40 region · 56 page. */
  size?: number;
  /** Ink by default; `currentColor` inside buttons and chips. */
  color?: string;
  /** The rings at rest, faint, under the drawing lines: for 40 and 56 over a busy ground. */
  track?: boolean;
  /** `double` is two rings, `triple` three. From 40 px up the Crista shows all four. */
  variant?: CristaSpinnerVariant;
  /** On a Dark Blue ground: the default ink turns paper. */
  onDark?: boolean;
  /** Announces the wait. Without it the spinner is decorative (`aria-busy` lives on its owner). */
  label?: string;
  sx?: SxProps<Theme>;
}

/**
 * The Crista without data (proposal D11, PF20): the rings of the living figure of Bodyprint+, in
 * one colour because a wait reads nothing. The lines draw themselves from the center to the edge
 * and fade together, in a loop (the neutral "Imprimir" of CR04); with reduced motion the drawn
 * figure only blinks, slowly. The same component in the web, the admin and the partners.
 */
export function CristaSpinner({
  size = 24,
  color = controlTokens.primary,
  track = false,
  variant = 'double',
  onDark = false,
  label,
  sx: sxProp,
}: CristaSpinnerProps) {
  const a11y = label ? { role: 'status', 'aria-label': label } : { 'aria-hidden': true };
  const rings = getCristaLoaderRings(getCristaLoaderRingCount(size, variant));
  const stroke = getCristaLoaderInk(color, onDark);
  return (
    <Box
      component="svg"
      viewBox="0 0 48 48"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={getCristaLoaderStroke(size)}
      {...a11y}
      sx={[sx.root(size), ...(Array.isArray(sxProp) ? sxProp : [sxProp])]}
    >
      {track
        ? rings.map((ring, index) => (
            <path key={`track-${index}`} d={ring.d} stroke={stroke} strokeOpacity={0.12} />
          ))
        : null}
      {rings.map((ring, index) => (
        <path
          key={`ring-${index}`}
          // `getCristaLoaderRings` lists the outermost first; the core draws first.
          className={`ring ring-${rings.length - 1 - index}`}
          d={ring.d}
          stroke={stroke}
          strokeOpacity={ring.opacity}
          pathLength={100}
          strokeDasharray="100 100"
        />
      ))}
    </Box>
  );
}

const draw = (index: number) =>
  `${CRISTA_LOADER_DRAWS[index]} ${CRISTA_LOADER_CYCLE_MS}ms linear infinite`;

const sx = {
  root: (size: number) => ({
    display: 'block',
    width: size,
    height: size,
    flexShrink: 0,
    overflow: 'visible',
    '& .ring': { strokeDashoffset: 100 },
    '& .ring-0': { animation: draw(0) },
    '& .ring-1': { animation: draw(1) },
    '& .ring-2': { animation: draw(2) },
    '& .ring-3': { animation: draw(3) },
    '@media (prefers-reduced-motion: reduce)': {
      '& .ring': {
        strokeDashoffset: 0,
        animation: `${cristaLoaderBlink} ${CRISTA_LOADER_BLINK_MS}ms ease-in-out infinite !important`,
      },
    },
  }),
};
